/**
 * A textarea that is always exactly as tall as its content.
 *
 * A fixed-height box with an inner scrollbar hides most of what you wrote the
 * moment it passes a few lines, which is the worst point at which to hide it:
 * long enough to matter, short enough that you expected to see it.
 *
 * Three details do the work. The height is reset to `auto` before every
 * measurement -- without that, scrollHeight is measured against the height we
 * last set, so the box can grow but never shrink. The border is added back,
 * because scrollHeight excludes it and `height` under border-box does not. And
 * a width change re-wraps the text, so it has to re-measure then too; the
 * observer watches width only, because reacting to the height changes it makes
 * itself would loop.
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import type { TextareaHTMLAttributes } from 'react'

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'rows'> & {
  value: string
}

export function AutoGrowTextarea({ value, className = '', ...rest }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const lastWidth = useRef(0)

  const fit = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    // scrollHeight excludes the border, but `height` under border-box includes
    // it, so assigning one to the other leaves the box short by exactly the
    // border and clips the last line by a couple of pixels.
    const style = getComputedStyle(el)
    const border =
      style.boxSizing === 'border-box'
        ? parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
        : 0
    el.style.height = `${el.scrollHeight + border}px`
  }, [])

  // Layout effect, not effect: this runs before the browser paints, so the box
  // is never briefly the wrong size. It matters on load, where the content
  // arrives from the API and would otherwise flash at one line first.
  useLayoutEffect(fit, [fit, value])

  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0
      if (width === lastWidth.current) return // our own height change; ignore it
      lastWidth.current = width
      fit()
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [fit])

  return (
    <textarea
      ref={ref}
      value={value}
      // Overflow hidden because there is never anything to scroll to, and no
      // drag handle because dragging fights a box that sets its own height.
      className={`resize-none overflow-hidden ${className}`}
      {...rest}
    />
  )
}
